import React from 'react';
import AppActionMenu from '../AppActionMenu';
import { appToast } from '../AppToast';

/**
 * ToolsButton — Shared ERP Toolbar Action Dropdown Button
 *
 * Props:
 *  - tools | items: Array of { key, label, icon, disabled }
 *  - onSelect: (key) => void callback on tool selection
 *  - label: Trigger button label (default: 'Tools')
 *  - icon: Material Symbols icon name (default: 'settings')
 *  - align: Dropdown panel alignment ('left' | 'right', default: 'right')
 *  - showChevron: Whether to show down chevron arrow (default: true)
 *  - triggerClassName: Custom CSS class for trigger button
 *  - className: Wrapper container class
 *  - disabled: Disable the trigger button
 */
export default function ToolsButton({
  tools = [],
  items = [],
  onSelect,
  label = 'Tools',
  icon = 'settings',
  align = 'right',
  showChevron = true,
  className = '',
  triggerClassName = '',
  menuClassName = '',
  disabled = false,
}) {
  const rawList = tools.length > 0 ? tools : items;

  if (!rawList || rawList.length === 0) {
    return null;
  }

  const toolList = rawList.map((tool) => {
    if (typeof tool === 'string') {
      let icon = 'build';
      const lower = tool.toLowerCase();
      if (lower.includes('excel')) icon = 'table_view';
      else if (lower.includes('pdf')) icon = 'picture_as_pdf';
      else if (lower.includes('print')) icon = 'print';
      else if (lower.includes('export')) icon = 'download';
      else if (lower.includes('import')) icon = 'upload';
      return { key: tool, label: tool, icon };
    }
    return {
      key: tool.key || tool.id || tool.label,
      label: tool.label || tool.name || tool.key,
      icon: tool.icon || 'build',
      disabled: tool.disabled,
    };
  });

  const defaultTriggerClass =
    'h-10 px-4 bg-surface-container-highest hover:bg-outline-variant/50 text-on-surface-variant font-bold rounded-xl text-xs flex items-center gap-2 cursor-pointer transition select-none shrink-0';

  const handleSelect = (key) => {
    if (onSelect) {
      onSelect(key);
    } else {
      const item = toolList.find((t) => t.key === key);
      const toolLabel = item ? item.label : key;
      appToast.info(`Executing tool: ${toolLabel}...`);
    }
  };

  return (
    <AppActionMenu
      triggerLabel={label}
      triggerIcon={icon}
      showChevron={showChevron}
      align={align}
      disabled={disabled}
      className={className}
      triggerClassName={triggerClassName || defaultTriggerClass}
      menuClassName={menuClassName}
      items={toolList}
      onSelect={handleSelect}
    />
  );
}
